import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Calendar,
  Download,
  Eye,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Server,
  ShieldCheck,
  ArrowUp,
  Plus,
  ExternalLink,
  CheckCircle2,
  Users,
  Cloud,
  Layers,
} from 'lucide-react';

const tabs = [
  'Overview',
  'Anchored Records',
  'IPFS Files',
  'Verifications',
  'Nodes',
  'Smart Contracts',
  'Settings',
];

const TAB_ROUTES = {
  overview: '/blockchain-anchoring',
  'anchored-records': '/blockchain-anchoring/anchored-records',
  'ipfs-files': '/blockchain-anchoring/ipfs-files',
  verifications: '/blockchain-anchoring/verifications',
  nodes: '/blockchain-anchoring/nodes',
  'smart-contracts': '/blockchain-anchoring/smart-contracts',
  settings: '/blockchain-anchoring/settings',
};

const SUMMARY_CARDS = [
  { title: 'Total Nodes', value: '12', change: '20% vs. last month', Icon: Server },
  { title: 'Active Nodes', value: '10', change: '25% vs. last month', Icon: CheckCircle2 },
  { title: 'IPFS Nodes', value: '7', change: '17% vs. last month', Icon: Cloud },
  { title: 'Blockchain Nodes', value: '5', change: '25% vs. last month', Icon: Layers },
];

const NODE_TYPES = ['All Node Types', 'IPFS', 'Blockchain'];
const STATUSES = ['All Statuses', 'Online', 'Offline', 'Maintenance'];
const REGIONS = ['All Regions', 'Asia', 'Europe', 'North America', 'Australia'];
const PAGE_SIZE = 12;

const baseNodes = [
  {
    id: '1', name: 'ipfs-node-01', type: 'IPFS', network: 'IPFS', ip: '10.10.1.12',
    location: 'Chennai, India', status: 'Online', uptime: '99.9%', version: 'v0.30.0',
    region: 'Asia', lastSeen: 'May 31, 2025 10:42 AM', nodeIdFull: 'QmX7a39c4e2b6d8e9f4a1c2b3d4e5f6a7b8c9d0e1f',
    cpu: '12%', memory: '34%', storage: '18%', connectedPeers: '8 / 12',
    logs: [
      { date: 'May 31, 2025 10:42 AM', text: 'Peer connected (QmR7...3f2a)' },
      { date: 'May 31, 2025 09:18 AM', text: 'Block synced (height: 8,742,501)' },
      { date: 'May 31, 2025 08:03 AM', text: 'IPFS pin added (QmZ4...9c8d)' },
    ],
  },
  {
    id: '2', name: 'ipfs-node-02', type: 'IPFS', network: 'IPFS', ip: '10.10.1.13',
    location: 'Bengaluru, India', status: 'Online', uptime: '99.7%', version: 'v0.30.0',
    region: 'Asia', lastSeen: 'May 31, 2025 10:30 AM', nodeIdFull: 'QmTF8v3h9kL2p6z1Rm4Xc8Yw3Nq5Ub7Ea9Dj2Gs6Hf1Kt',
    cpu: '15%', memory: '40%', storage: '22%', connectedPeers: '10 / 12', logs: [],
  },
  {
    id: '3', name: 'ipfs-node-03', type: 'IPFS', network: 'IPFS', ip: '10.10.1.14',
    location: 'Mumbai, India', status: 'Online', uptime: '99.6%', version: 'v0.30.0',
    region: 'Asia', lastSeen: 'May 31, 2025 10:25 AM', nodeIdFull: 'QmZ8k6p9w1n4t7e2Qa5Sd8Fg3Hj6Kl9Zx2Cv5Bn8Mq1Wr',
    cpu: '10%', memory: '28%', storage: '15%', connectedPeers: '9 / 12', logs: [],
  },
  {
    id: '4', name: 'eth-node-01', type: 'Blockchain', network: 'Ethereum', ip: '192.168.1.10',
    location: 'Frankfurt, Germany', status: 'Online', uptime: '99.8%', version: 'Geth/v1.11.6',
    region: 'Europe', lastSeen: 'May 31, 2025 10:40 AM', nodeIdFull: '0x7f3a9c4e2b6d8e9f4a1c2b3d4e5f6a7b8c9d0e1f',
    cpu: '45%', memory: '68%', storage: '52%', connectedPeers: '35 / 40', logs: [],
  },
  {
    id: '5', name: 'eth-node-02', type: 'Blockchain', network: 'Ethereum', ip: '192.168.1.11',
    location: 'Singapore', status: 'Online', uptime: '99.5%', version: 'Geth/v1.11.6',
    region: 'Asia', lastSeen: 'May 31, 2025 10:35 AM', nodeIdFull: '0x9a2b4c6d7e8f1a2b3c4d5e6f7a8b9c0d1e2f3a4b',
    cpu: '42%', memory: '65%', storage: '50%', connectedPeers: '32 / 40', logs: [],
  },
  {
    id: '6', name: 'eth-node-03', type: 'Blockchain', network: 'Ethereum', ip: '192.168.1.12',
    location: 'New York, USA', status: 'Online', uptime: '99.4%', version: 'Geth/v1.11.5',
    region: 'North America', lastSeen: 'May 31, 2025 10:20 AM', nodeIdFull: '0x3d8f61a1c2e7b9f0a1b2c3d4e5f6a7b8c9d0e1f2',
    cpu: '48%', memory: '70%', storage: '55%', connectedPeers: '30 / 40', logs: [],
  },
  {
    id: '7', name: 'ipfs-node-04', type: 'IPFS', network: 'IPFS', ip: '10.20.1.21',
    location: 'Dubai, UAE', status: 'Online', uptime: '99.8%', version: 'v0.30.0',
    region: 'Asia', lastSeen: 'May 31, 2025 10:33 AM', nodeIdFull: 'QmR7t3n9p5v1k2o6Fg2Hj5Kl8Zx1Cv4Bn7Mq9Ws3Ed6Rt2',
    cpu: '14%', memory: '32%', storage: '19%', connectedPeers: '11 / 12', logs: [],
  },
  {
    id: '8', name: 'ipfs-node-05', type: 'IPFS', network: 'IPFS', ip: '10.20.1.22',
    location: 'London, UK', status: 'Online', uptime: '99.3%', version: 'v0.30.0',
    region: 'Europe', lastSeen: 'May 31, 2025 10:15 AM', nodeIdFull: 'QmF6p1t8v9z3k2m7Ns2Bv5Cx8Zl1Km4Jh7Gf9Ds3Aq6Wr',
    cpu: '18%', memory: '38%', storage: '24%', connectedPeers: '7 / 12', logs: [],
  },
  {
    id: '9', name: 'eth-node-04', type: 'Blockchain', network: 'Ethereum', ip: '192.168.2.10',
    location: 'Tokyo, Japan', status: 'Maintenance', uptime: '94.2%', version: 'Geth/v1.11.6',
    region: 'Asia', lastSeen: 'May 31, 2025 09:00 AM', nodeIdFull: '0x8e4c7d2a9f1b3c5e7a9b0c1d2e3f4a5b6c7d8e9f',
    cpu: '5%', memory: '20%', storage: '48%', connectedPeers: '0 / 40', logs: [],
  },
  {
    id: '10', name: 'eth-node-05', type: 'Blockchain', network: 'Ethereum', ip: '192.168.2.11',
    location: 'Toronto, Canada', status: 'Online', uptime: '99.6%', version: 'Geth/v1.11.6',
    region: 'North America', lastSeen: 'May 31, 2025 10:28 AM', nodeIdFull: '0x6b2f9e8d7c1a4f3e5a6b7c8d9e0f1a2b3c4d5e6f',
    cpu: '39%', memory: '60%', storage: '45%', connectedPeers: '34 / 40', logs: [],
  },
  {
    id: '11', name: 'ipfs-node-06', type: 'IPFS', network: 'IPFS', ip: '10.30.1.31',
    location: 'Sydney, Australia', status: 'Online', uptime: '99.7%', version: 'v0.30.0',
    region: 'Australia', lastSeen: 'May 31, 2025 10:31 AM', nodeIdFull: 'QmP9k2v7n5t4e8z1Hd3Fg6Jk9Lq2Zx5Cv8Bn1Mw4Er7Ty',
    cpu: '11%', memory: '29%', storage: '16%', connectedPeers: '10 / 12', logs: [],
  },
  {
    id: '12', name: 'ipfs-node-07', type: 'IPFS', network: 'IPFS', ip: '10.30.1.32',
    location: 'San Jose, USA', status: 'Offline', uptime: '0.0%', version: 'v0.29.1',
    region: 'North America', lastSeen: 'May 30, 2025 08:00 PM', nodeIdFull: 'QmT5r8p3k1n6v9e2Pd7Fs3Gh9Jk2Lz5Xc8Vb1Nm4Qw6Er',
    cpu: '0%', memory: '0%', storage: '14%', connectedPeers: '0 / 12', logs: [],
  },
];

export default function NodesPage() {
  const navigate = useNavigate();

  const [activeTab] = useState('nodes');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All Node Types');
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const [regionFilter, setRegionFilter] = useState('All Regions');
  const [selectedNode, setSelectedNode] = useState(baseNodes[0]);
  const [page, setPage] = useState(1);

  const filteredNodes = useMemo(() => {
    const query = search.trim().toLowerCase();
    return baseNodes.filter((node) => {
      const matchesSearch =
        !query ||
        node.name.toLowerCase().includes(query) ||
        node.ip.toLowerCase().includes(query) ||
        node.location.toLowerCase().includes(query);
      const matchesType = typeFilter === 'All Node Types' || node.type === typeFilter;
      const matchesStatus = statusFilter === 'All Statuses' || node.status === statusFilter;
      const matchesRegion = regionFilter === 'All Regions' || node.region === regionFilter;
      return matchesSearch && matchesType && matchesStatus && matchesRegion;
    });
  }, [search, typeFilter, statusFilter, regionFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredNodes.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visibleNodes = filteredNodes.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const firstNode = filteredNodes.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const lastNode = Math.min(currentPage * PAGE_SIZE, filteredNodes.length);

  const updateFilter = (setter, value) => { setter(value); setPage(1); };

  return (
    <div className="min-h-screen bg-[#f7fbff] text-[#1e3559]">
      {/* Top Tabs Header */}
      <header className="sticky top-0 z-40 bg-white">
        <div className="mx-auto flex h-14 max-w-[1600px] items-center justify-between px-0">
          <nav className="flex gap-6 overflow-x-auto">
            {tabs.map((tab) => {
              const tabKey = tab.toLowerCase().replace(/\s+/g, '-');
              const isActive = activeTab === tabKey;
              return (
                <button
                  type="button"
                  key={tab}
                  onClick={() => navigate(TAB_ROUTES[tabKey])}
                  className={`whitespace-nowrap pb-3 pt-1 text-xs font-medium transition ${
                    isActive ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-900 hover:text-gray-900'
                  }`}
                >
                  {tab}
                </button>
              );
            })}
          </nav>

          <button
            type="button"
            className="flex h-7 shrink-0 items-center gap-1.5 rounded-md bg-[#6200ee] px-3 text-[10px] font-medium text-white shadow-sm transition hover:bg-[#5100c9]"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Node
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] space-y-4 px-0 pb-5">
        {/* Top Summary Cards & Network Health Grid */}
        <section className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1fr)_330px]">
          {/* 4 Summary Cards refactored into individual bordered cards */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {SUMMARY_CARDS.map((card) => {
              const Icon = card.Icon;
              return (
                <div
                  key={card.title}
                  className="min-w-0 rounded-md border border-[#e5edf7] bg-white px-3 py-2 shadow-sm"
                >
                  <div className="flex items-center gap-2">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-[#f2edff]">
                      <Icon className="h-7 w-7 text-[#6426df]" strokeWidth={1.8} />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-[11px] font-medium text-[#7290b5] leading-tight">{card.title}</p>
                      <p className="text-xl font-bold leading-tight text-[#20375e]">{card.value}</p>
                      <p className="text-[9px] leading-tight text-[#21a675]">
                        <ArrowUp className="h-3 w-3 inline" strokeWidth={2} />
                        {card.change}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Network Health Card on Top Right */}
          <div className="flex items-center justify-between rounded-md border border-[#e5edf7] bg-white px-3 py-2 shadow-sm">
            <div className="flex items-center gap-2">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-[#f2edff]">
                <ShieldCheck className="h-7 w-7 text-[#6426df]" strokeWidth={1.8} />
              </div>
              <div className="min-w-0">
                <p className="truncate text-[11px] font-medium text-[#7290b5] leading-tight">Network Health</p>
                <p className="text-xl font-bold leading-tight text-[#20375e]">98%</p>
                <p className="text-[9px] leading-tight text-[#21a675]">
                  <ArrowUp className="h-3 w-3 inline" strokeWidth={2} /> 5% vs. last month
                </p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#dff7ea] px-2.5 py-1 text-[10px] font-medium text-[#29996e]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#29996e]" /> Online
            </span>
          </div>
        </section>

        {/* Table + Sidebar Layout */}
        <section className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_260px] xl:grid-cols-[minmax(0,1fr)_270px]">
          {/* Left Column: Table & Bottom Cards */}
          <div className="space-y-4">
            {/* Table Card */}
            <div className="min-w-0 overflow-hidden rounded-md border border-[#e5edf7] bg-white shadow-sm">
              {/* Filters Bar */}
              <div className="flex flex-wrap items-center gap-1.5 border-b border-[#edf2f8] bg-[#f8fbff] px-3 py-2">
                <div className="relative min-w-[160px] flex-1">
                  <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-[#86a0be]" />
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => updateFilter(setSearch, e.target.value)}
                    placeholder="Search by node name, IP, or location..."
                    className="h-6 w-full rounded border border-[#dfe9f4] bg-white pl-6 pr-2 text-[10px] text-[#49678f] outline-none placeholder:text-[#91a5bf] focus:border-[#8d62ea]"
                  />
                </div>
                <div className="relative">
                  <select value={typeFilter} onChange={(e) => updateFilter(setTypeFilter, e.target.value)} className="h-6 appearance-none rounded border border-[#dfe9f4] bg-white pl-2 pr-5 text-[10px] text-[#5b769c] outline-none focus:border-[#8d62ea]">
                    {NODE_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-1 top-1/2 h-2.5 w-2.5 -translate-y-1/2 text-[#87a0be]" />
                </div>
                <div className="relative">
                  <select value={statusFilter} onChange={(e) => updateFilter(setStatusFilter, e.target.value)} className="h-6 appearance-none rounded border border-[#dfe9f4] bg-white pl-2 pr-5 text-[10px] text-[#5b769c] outline-none focus:border-[#8d62ea]">
                    {STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-1 top-1/2 h-2.5 w-2.5 -translate-y-1/2 text-[#87a0be]" />
                </div>
                <div className="relative">
                  <select value={regionFilter} onChange={(e) => updateFilter(setRegionFilter, e.target.value)} className="h-6 appearance-none rounded border border-[#dfe9f4] bg-white pl-2 pr-5 text-[10px] text-[#5b769c] outline-none focus:border-[#8d62ea]">
                    {REGIONS.map((r) => <option key={r}>{r}</option>)}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-1 top-1/2 h-2.5 w-2.5 -translate-y-1/2 text-[#87a0be]" />
                </div>
                <button type="button" className="flex h-6 items-center gap-1 rounded border border-[#dfe9f4] bg-white px-2 text-[10px] text-[#5b769c] hover:bg-white">
                  <Calendar className="h-3 w-3 text-[#718daf]" />
                  May 1 – May 31, 2025
                  <ChevronDown className="h-2.5 w-2.5 text-[#87a0be]" />
                </button>
                <button type="button" className="flex h-6 items-center gap-1 rounded border border-[#dfe9f4] bg-white px-2 text-[10px] font-medium text-[#6426df] hover:bg-violet-50">
                  <Download className="h-3 w-3" />
                  Export
                </button>
              </div>

              {/* Table */}
              <div className="overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <table className="w-full table-fixed border-collapse">
                  <colgroup>
                    <col className="w-[38px]" />
                    <col className="w-[18%]" />
                    <col className="w-[10%]" />
                    <col className="w-[12%]" />
                    <col className="w-[14%]" />
                    <col className="w-[16%]" />
                    <col className="w-[10%]" />
                    <col className="w-[8%]" />
                    <col className="w-[65px]" />
                  </colgroup>
                  <thead>
                    <tr className="h-9 border-b border-[#edf2f8] bg-[#f8fbff] text-left text-[10px] font-semibold uppercase tracking-wide text-[#7791b3]">
                      <th className="w-[38px] px-2 text-center">#</th>
                      <th className="px-2">Node Name</th>
                      <th className="px-2">Type</th>
                      <th className="px-2">Network</th>
                      <th className="px-2">IP Address</th>
                      <th className="px-2">Location</th>
                      <th className="px-2">Status</th>
                      <th className="px-2">Uptime</th>
                      <th className="w-[65px] px-2 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleNodes.map((node, index) => {
                      const isSelected = selectedNode?.id === node.id;
                      return (
                        <tr
                          key={node.id}
                          onClick={() => setSelectedNode(node)}
                          className={`h-[50px] cursor-pointer border-b border-[#f0f4f8] transition hover:bg-[#f9fbff] ${isSelected ? 'bg-[#fbfaff]' : 'bg-white'}`}
                        >
                          <td className="w-[38px] px-2 text-center text-xs font-medium text-[#58749c]">
                            {(currentPage - 1) * PAGE_SIZE + index + 1}
                          </td>
                          <td className="px-2">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-[#f2edff]">
                                {node.type === 'IPFS' ? <Cloud className="h-3.5 w-3.5 text-[#6426df]" /> : <Layers className="h-3.5 w-3.5 text-[#6426df]" />}
                              </div>
                              <span className="truncate text-xs font-medium text-[#46658e]" title={node.name}>{node.name}</span>
                            </div>
                          </td>
                          <td className="px-2">
                            <span className="text-xs text-[#56729a]">{node.type}</span>
                          </td>
                          <td className="px-2">
                            <span className="inline-flex rounded bg-[#f0f4f8] px-1.5 py-0.5 text-[11px] font-medium text-[#55719a]">
                              {node.network}
                            </span>
                          </td>
                          <td className="px-2">
                            <span className="truncate font-mono text-[11px] text-[#6f88a8]">{node.ip}</span>
                          </td>
                          <td className="px-2">
                            <span className="truncate text-xs text-[#56729a]" title={node.location}>{node.location}</span>
                          </td>
                          <td className="px-2">
                            <StatusBadge status={node.status} />
                          </td>
                          <td className="px-2">
                            <span className="text-xs text-[#56729a]">{node.uptime}</span>
                          </td>
                          <td className="w-[65px] px-2">
                            <div className="flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                onClick={() => setSelectedNode(node)}
                                title="View node details"
                                className="text-[#7c9fca] transition hover:text-[#6426df]"
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                title="More actions"
                                className="text-[#8ca4c2] transition hover:text-[#6426df]"
                              >
                                <MoreVertical className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {visibleNodes.length === 0 && (
                      <tr>
                        <td colSpan={9} className="h-64 text-center text-xs text-[#8ca2bd]">No nodes found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex min-h-[52px] flex-wrap items-center justify-between gap-3 border-t border-[#edf2f8] px-4 py-3">
                <p className="text-[10px] text-[#6682aa]">
                  Showing {firstNode}-{lastNode} of {filteredNodes.length} nodes
                </p>
                <nav aria-label="Nodes pagination" className="flex items-center gap-1">
                  <PaginationButton disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)} label="Previous page">
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </PaginationButton>
                  <PaginationButton active onClick={() => setPage(1)} label="Page 1">
                    1
                  </PaginationButton>
                  <PaginationButton disabled onClick={() => {}} label="Next page">
                    <ChevronRight className="h-3.5 w-3.5" />
                  </PaginationButton>
                </nav>
              </div>
            </div>

            {/* Bottom Section: Node Network Map & Network Summary */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
              {/* Node Network Map Card with Image Background */}
              <div className="rounded-md border border-[#e5edf7] bg-white p-4 shadow-sm">
                <h3 className="mb-3 text-xs font-bold text-[#29446c]">Node Network</h3>
                <div className="relative flex h-44 w-full items-center justify-center overflow-hidden rounded bg-[#f8fbff] border border-[#edf2f8]">
                  <img
                    src="https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?auto=format&fit=crop&w=1200&q=80"
                    alt="World Map Network"
                    className="absolute inset-0 h-full w-full object-cover opacity-40"
                  />
                  <span className="absolute top-12 left-1/4 h-2.5 w-2.5 rounded-full bg-[#29996e] shadow ring-2 ring-white" title="Chennai, India" />
                  <span className="absolute top-16 left-1/2 h-2.5 w-2.5 rounded-full bg-[#29996e] shadow ring-2 ring-white" title="Frankfurt, Germany" />
                  <span className="absolute bottom-12 right-1/3 h-2.5 w-2.5 rounded-full bg-amber-500 shadow ring-2 ring-white" title="Tokyo, Japan" />
                  <span className="absolute bottom-10 left-1/5 h-2.5 w-2.5 rounded-full bg-rose-500 shadow ring-2 ring-white" title="San Jose, USA" />
                  <div className="absolute bottom-2 left-3 flex flex-wrap gap-3 text-[9px] text-[#6d83a4] bg-white/80 px-2 py-1 rounded backdrop-blur-xs">
                    <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#29996e]" /> IPFS Node</span>
                    <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#6426df]" /> Blockchain Node</span>
                    <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-500" /> Maintenance</span>
                    <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-rose-500" /> Offline</span>
                  </div>
                </div>
              </div>
              {/* Network Summary Card */}
              <div className="rounded-md border border-[#e5edf7] bg-white p-4 shadow-sm flex flex-col justify-between">
                <div>
                  <h3 className="mb-3 text-xs font-bold text-[#29446c]">Network Summary</h3>
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[#8198b7]">Total Nodes</span>
                      <span className="font-semibold text-[#29446c]">12</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[#8198b7]">IPFS Nodes</span>
                      <span className="font-semibold text-[#29446c]">7</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[#8198b7]">Blockchain Nodes</span>
                      <span className="font-semibold text-[#29446c]">5</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[#8198b7]">Regions</span>
                      <span className="font-semibold text-[#29446c]">6</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[#8198b7]">Countries</span>
                      <span className="font-semibold text-[#29446c]">5</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right-Side Node Details Sidebar */}
          {selectedNode && <NodeDetails node={selectedNode} />}
        </section>
      </main>
    </div>
  );
}

function PaginationButton({ children, onClick, disabled = false, active = false, label }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      className={`flex h-7 w-7 items-center justify-center rounded border text-[10px] font-medium transition ${
        active
          ? 'border-[#6200ee] bg-[#6200ee] text-white'
          : 'border-[#e2eaf4] bg-white text-[#718caf] hover:bg-[#f7faff]'
      } disabled:cursor-not-allowed disabled:opacity-40`}
    >
      {children}
    </button>
  );
}

function StatusBadge({ status }) {
  const styles = {
    Online: 'bg-[#dff7ea] text-[#29996e]',
    Maintenance: 'bg-amber-50 text-amber-600',
    Offline: 'bg-rose-50 text-rose-600',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[9px] font-medium ${styles[status] || 'bg-slate-100 text-slate-600'}`}>
      {status === 'Online' && <span className="h-1.5 w-1.5 rounded-full bg-[#29996e]" />}
      {status === 'Maintenance' && <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />}
      {status === 'Offline' && <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />}
      {status}
    </span>
  );
}

function DetailRow({ label, value, children, link = false, compact = false }) {
  return (
    <div className="grid grid-cols-[90px_minmax(0,1fr)] items-start gap-2">
      <span className="text-[11px] leading-[15px] text-[#8198b7]">{label}</span>
      {children ? (
        <div className="min-w-0">{children}</div>
      ) : (
        <div className="flex min-w-0 items-start gap-1">
          <span
            title={value}
            className={`min-w-0 break-all leading-[15px] ${compact ? 'font-mono text-[10px]' : 'text-[11px]'} ${link ? 'text-[#6426df]' : 'text-[#55719a]'}`}
          >
            {value}
          </span>
          {link && <ExternalLink className="mt-0.5 h-2.5 w-2.5 shrink-0 text-[#6426df]" />}
        </div>
      )}
    </div>
  );
}

function NodeDetails({ node }) {
  return (
    <aside className="min-w-0 rounded-md border border-[#e5edf7] bg-white p-4 shadow-sm space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-bold text-[#29446c]">Node Details</h2>
        <StatusBadge status={node.status} />
      </div>

      {/* Node Identity */}
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#f2edff]">
          <Server className="h-5 w-5 text-[#6426df]" />
        </div>
        <div className="min-w-0">
          <h3 title={node.name} className="truncate text-xs font-semibold text-[#29446c]">{node.name}</h3>
          <p className="mt-0.5 text-[11px] text-[#8299b8]">{node.type} Node</p>
        </div>
      </div>

      {/* Basic Node Info */}
      <div className="space-y-2 border-t border-[#eaf0f7] pt-3">
        <DetailRow label="Node ID" value={node.nodeIdFull} link compact />
        <DetailRow label="Type" value={node.type} />
        <DetailRow label="Network" value={node.network} />
        <DetailRow label="IP Address" value={node.ip} />
        <DetailRow label="Location" value={node.location} />
        <DetailRow label="Region" value={node.region} />
        <DetailRow label="Version" value={node.version} />
        <DetailRow label="Uptime" value={node.uptime} />
        <DetailRow label="Last Seen" value={node.lastSeen} />
      </div>

      {/* Resource Usage Section */}
      <div className="border-t border-[#eaf0f7] pt-3 space-y-2.5">
        <h4 className="text-[11px] font-semibold text-[#29446c]">Resource Usage</h4>

        <div>
          <div className="mb-1 flex justify-between text-[10px]">
            <span className="text-[#8198b7]">CPU</span>
            <span className="font-medium text-[#55719a]">{node.cpu}</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#f0f4f8]">
            <div className="h-full rounded-full bg-[#6426df]" style={{ width: node.cpu }} />
          </div>
        </div>

        <div>
          <div className="mb-1 flex justify-between text-[10px]">
            <span className="text-[#8198b7]">Memory</span>
            <span className="font-medium text-[#55719a]">{node.memory}</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#f0f4f8]">
            <div className="h-full rounded-full bg-[#29996e]" style={{ width: node.memory }} />
          </div>
        </div>

        <div>
          <div className="mb-1 flex justify-between text-[10px]">
            <span className="text-[#8198b7]">Storage</span>
            <span className="font-medium text-[#55719a]">{node.storage}</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#f0f4f8]">
            <div className="h-full rounded-full bg-[#6426df]" style={{ width: node.storage }} />
          </div>
        </div>
      </div>

      {/* Connected Peers */}
      <div className="border-t border-[#eaf0f7] pt-3 flex items-center justify-between text-xs">
        <span className="flex items-center gap-1.5 text-[#8198b7]">
          <Users className="h-3.5 w-3.5 text-[#6426df]" /> Connected Peers
        </span>
        <span className="font-semibold text-[#29446c]">{node.connectedPeers}</span>
      </div>

      {/* Blockchain/IPFS Logs */}
      {node.logs && node.logs.length > 0 && (
        <div className="border-t border-[#eaf0f7] pt-3 space-y-2">
          <h4 className="text-[11px] font-semibold text-[#29446c]">Recent Logs</h4>
          <div className="space-y-1.5 max-h-32 overflow-y-auto">
            {node.logs.map((log) => (
              <div key={`${log.date}-${log.text}`} className="rounded border border-[#edf2f8] bg-[#f8fbff] p-2 text-[10px]">
                <p className="font-medium text-[#46658e]">{log.text}</p>
                <p className="mt-0.5 text-[9px] text-[#8098b6]">{log.date}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </aside>
  );
}